/**
 * Дефолти у шапці застосунку (рішення власника 2026-07-25):
 * «Організація за замовчуванням» + «Період за замовчуванням».
 *
 * - Орг: підставляється першою у всіх документах/звітах (через сортування
 *   в usePermissionsByOrg). Юзер у документі може обрати іншу.
 * - Період: місяць / квартал / рік / свій / усі дати — фільтрує сторінки-
 *   списки документів (useDefaultPeriod).
 *
 * КЛІЄНТ (ч.1075, рішення власника): якщо в користувача доступ до кількох
 * акаунтів — перед організацією стоїть фільтр клієнта (TenantSelect) з
 * власними ярликами. Список організацій — лише поточного клієнта. Замінює
 * «розумний перемикач» ч.1068 (організації всіх клієнтів в одному списку,
 * згруповані по клієнтах) — схожі назви клієнтів у ньому плутались.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import TenantSelect from '@/components/TenantSelect';
import { useEffectiveMode } from '@/hooks/useAccountMode';
import { useDefaults, periodRange, type PeriodMode } from '@/store/defaults';

const PERIOD_LABELS: Record<PeriodMode, string> = {
  all: 'усі дати',
  month: 'місяць',
  quarter: 'квартал',
  year: 'рік',
  custom: 'свій період',
};

export default function HeaderDefaults() {
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // ч.1405 (рішення власника): «ЕДО» — лише організація (картка учасника); «Бізнес» — організація
  // і період, без вибору акаунта (його ховає TenantSelect); «Користувач» — чужий акаунт + організація + період.
  const edoMode = useEffectiveMode() === 'EDO';
  const {
    defaultOrgId, setDefaultOrg,
    periodMode, setPeriodMode,
    customFrom, customTo, setCustomRange,
    periodYear, setPeriodYear,
    periodMonth, setPeriodMonth,
    periodQuarter, setPeriodQuarter,
  } = useDefaults();

  const range = periodRange(periodMode, customFrom, customTo,
    { year: periodYear, month: periodMonth, quarter: periodQuarter });

  const currentOrgs = orgsQ.data?.items ?? [];
  // ч.1229 (баг власника): збережена організація, якої в цьому клієнті немає (видалена,
  // забрали доступ, лишилась від іншого клієнта), давала ПОРОЖНЄ поле, а всі сторінки
  // підставляли її у запити — помилка. Скидаємо на «не обрано», щойно знаємо ПОВНИЙ
  // перелік організацій клієнта (неповну сторінку переліку за доказ не вважаємо).
  const orgsData = orgsQ.data;
  useEffect(() => {
    if (!orgsData || !defaultOrgId) return;
    const complete = orgsData.items.length >= (orgsData.total ?? orgsData.items.length);
    if (complete && !orgsData.items.some((o) => o.id === defaultOrgId)) setDefaultOrg('');
  }, [orgsData, defaultOrgId, setDefaultOrg]);
  // Роки: від 2016 (глибина міграції) до наступного.
  const years: number[] = [];
  for (let yy = new Date().getFullYear() + 1; yy >= 2016; yy--) years.push(yy);
  const MONTHS = ['січень', 'лютий', 'березень', 'квітень', 'травень',
    'червень', 'липень', 'серпень', 'вересень', 'жовтень',
    'листопад', 'грудень'];

  return (
    <div className="app__hf-row">
      <TenantSelect />
      {(!edoMode || currentOrgs.length > 0) && (
      <div className="app__hf app__hf--org">
      <select
        value={defaultOrgId}
        onChange={(e) => setDefaultOrg(e.target.value)}
        aria-label={edoMode ? 'Картка учасника ЕДО за замовчуванням' : 'Організація за замовчуванням'}
        title={edoMode ? 'Картка учасника ЕДО за замовчуванням' : 'Організація за замовчуванням — підставляється у нові документи і звіти'}
      >
        <option value="">{edoMode ? '— картка за замовчуванням —' : '— орг за замовчуванням —'}</option>
        {currentOrgs.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      </div>
      )}
      {edoMode ? null : (<>
      <div className="app__hf app__hf--period">
      <select
        value={periodMode}
        onChange={(e) => setPeriodMode(e.target.value as PeriodMode)}
        aria-label="Період документів"
        title={range.from
          ? `Період списків документів: ${range.from} — ${range.to}`
          : 'Період списків документів: без обмеження'}
      >
        {(Object.keys(PERIOD_LABELS) as PeriodMode[]).map((m) => (
          <option key={m} value={m}>{PERIOD_LABELS[m]}</option>
        ))}
      </select>
      </div>

      {/* 2026-08-09 (рішення власника): місяць/квартал/рік ОБИРАЮТЬСЯ. */}
      {periodMode === 'month' && (
        <div className="app__hf-group">
          <div className="app__hf app__hf--month"><select value={periodMonth} aria-label="Місяць періоду"
            onChange={(e) => setPeriodMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select></div>
          <div className="app__hf app__hf--year"><select value={periodYear} aria-label="Рік періоду"
            onChange={(e) => setPeriodYear(Number(e.target.value))}>
            {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
          </select></div>
        </div>
      )}
      {periodMode === 'quarter' && (
        <div className="app__hf-group">
          <div className="app__hf app__hf--month"><select value={periodQuarter} aria-label="Квартал періоду"
            onChange={(e) => setPeriodQuarter(Number(e.target.value))}>
            {[1, 2, 3, 4].map((q) => (
              <option key={q} value={q}>{q} квартал</option>
            ))}
          </select></div>
          <div className="app__hf app__hf--year"><select value={periodYear} aria-label="Рік періоду"
            onChange={(e) => setPeriodYear(Number(e.target.value))}>
            {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
          </select></div>
        </div>
      )}
      {periodMode === 'year' && (
        <div className="app__hf app__hf--year"><select value={periodYear} aria-label="Рік періоду"
          onChange={(e) => setPeriodYear(Number(e.target.value))}>
          {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
        </select></div>
      )}
      {periodMode === 'custom' && (
        <div className="app__hf-group">
          <div className="app__hf app__hf--date"><DatePopover value={customFrom}
            onChange={(v) => setCustomRange(v, customTo)} /></div>
          <span style={{ color: 'var(--muted)' }}>—</span>
          <div className="app__hf app__hf--date"><DatePopover value={customTo}
            onChange={(v) => setCustomRange(customFrom, v)} /></div>
        </div>
      )}
      </>)}
    </div>
  );
}
